
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getReportHistory, CertificateReport, ReportStatus } from '@/utils/reportUtils';
import { useIsMobile } from '@/hooks/use-mobile';

interface ReportHistoryProps {
  onDownload: (reportId: string, state: string, year: number, quarter: number) => void;
  onMarkAsSubmitted: (reportId: string) => void;
}

export function ReportHistory({ onDownload, onMarkAsSubmitted }: ReportHistoryProps) {
  const { data: reportHistory, isLoading } = useQuery({
    queryKey: ['reportHistory'],
    queryFn: async () => await getReportHistory(),
  });
  
  const isMobile = useIsMobile();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Report History</CardTitle>
        <CardDescription>
          Previously generated reports. Reports are stored to comply with regulatory requirements.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>State</TableHead>
              <TableHead>Year</TableHead>
              {!isMobile && <TableHead>Quarter</TableHead>}
              {!isMobile && <TableHead>Format</TableHead>}
              <TableHead>Status</TableHead>
              {!isMobile && <TableHead>Generated On</TableHead>}
              {!isMobile && <TableHead>Generated By</TableHead>}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 4 : 8} className="text-center py-4">Loading report history...</TableCell>
              </TableRow>
            ) : reportHistory?.data && reportHistory.data.length > 0 ? (
              reportHistory.data.map((report: CertificateReport) => (
                <TableRow key={report.id}>
                  <TableCell>{report.state}</TableCell>
                  <TableCell>{report.year}{isMobile ? ` Q${report.quarter}` : ''}</TableCell>
                  {!isMobile && <TableCell>Q{report.quarter}</TableCell>}
                  {!isMobile && <TableCell>{report.format}</TableCell>}
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                      ${report.status === ReportStatus.SUBMITTED ? 'bg-green-100 text-green-800' :
                        report.status === ReportStatus.REJECTED ? 'bg-red-100 text-red-800' :
                        report.status === ReportStatus.ACCEPTED ? 'bg-blue-100 text-blue-800' : 
                        'bg-gray-100 text-gray-800'}`}>
                      {report.status || ReportStatus.DRAFT}
                    </span>
                  </TableCell>
                  {!isMobile && <TableCell>{format(new Date(report.generated_at || ''), 'MMM d, yyyy')}</TableCell>}
                  {!isMobile && <TableCell>{report.generated_by || 'System'}</TableCell>}
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDownload(report.id || '', report.state, report.year, report.quarter)}
                      title="Download report"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {(!report.status || report.status === ReportStatus.DRAFT) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onMarkAsSubmitted(report.id || '')}
                        title="Mark as submitted"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={isMobile ? 4 : 8} className="text-center py-4">No reports have been generated yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
